import { WebSocketStatus } from "@hocuspocus/provider";
import { action, makeAutoObservable } from "mobx";

import type { HocuspocusProvider } from "@hocuspocus/provider";
import type { Editor as TiptapEditor } from "@tiptap/core";
import type * as Y from "yjs";

export type ConnectionStatusType =
  | "no-connection"
  | "offline"
  | "offline-unsynced"
  | "online"
  | "online-unsynced"
  | "reconnecting";

export class ConnectionStatusModel {
  status: ConnectionStatusType = "no-connection";

  private isBrowserOffline = false;

  private hasLocalChanges = false;

  showCurrentStatus = false;

  private hideStatusTimeout?: NodeJS.Timeout;

  setupEditorChangeListener?: () => void;

  constructor(
    private yDoc?: Y.Doc | null,
    private yProvider?: HocuspocusProvider,
  ) {
    makeAutoObservable(
      this,
      {
        setupEditorChangeListener: false,
      },
      { deep: false },
    );
  }

  setupConnectionStatusTracking() {
    const provider = this.yProvider;
    if (!provider) {
      this.status = "no-connection";
      return;
    }

    const updateConnectionStatus = action(() => {
      const { isSynced } = provider;
      const { status } = provider.configuration.websocketProvider;
      if (this.hideStatusTimeout) {
        clearTimeout(this.hideStatusTimeout);
        this.hideStatusTimeout = undefined;
      }

      const currentStatus = this.status;

      if (this.isBrowserOffline || status === WebSocketStatus.Disconnected) {
        if (!provider.isSynced || this.hasLocalChanges) {
          this.status = "offline-unsynced";
          this.showCurrentStatus = true;
        } else {
          this.status = "offline";
          this.showCurrentStatus = true;
        }
      } else if (status === WebSocketStatus.Connecting) {
        this.status = "reconnecting";
        this.showCurrentStatus = true;
      } else if (status === WebSocketStatus.Connected && !isSynced) {
        this.status = "online-unsynced";
        this.showCurrentStatus = true;
      } else if (status === WebSocketStatus.Connected && isSynced) {
        this.status = "online";
        this.hasLocalChanges = false;

        if (currentStatus !== "no-connection") {
          this.showCurrentStatus = true;
          this.scheduleHideStatus();
        }
      }
    });

    const handleStatus = action(
      (_event: { status: "connecting" | "connected" | "disconnected" }) => {
        updateConnectionStatus();
      },
    );

    const handleConnect = action(() => {
      updateConnectionStatus();
    });

    const handleDisconnect = action(() => {
      updateConnectionStatus();
    });

    const handleSynced = action((state?: boolean) => {
      if (typeof state === "boolean") {
        provider.isSynced = state;
      }
      updateConnectionStatus();
    });

    // Listen for document changes to detect local modifications
    const handleDocumentUpdate = action((update: Uint8Array, origin: string | null) => {
      // Only track local changes (not from remote or provider)
      if (origin !== "remote" && origin !== "provider" && origin !== "y-sync$") {
        this.hasLocalChanges = true;
        if (this.status === "offline" || this.status === "offline-unsynced") {
          updateConnectionStatus();
        }
      }
    });

    const handleOnline = action(() => {
      this.isBrowserOffline = false;
      updateConnectionStatus();
    });

    const handleOffline = action(() => {
      this.isBrowserOffline = true;
      updateConnectionStatus();
    });

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    provider.on("status", handleStatus);
    provider.on("connect", handleConnect);
    provider.on("disconnect", handleDisconnect);
    provider.on("synced", handleSynced);

    // Listen to Yjs document changes
    if (this.yDoc) {
      this.yDoc.on("update", handleDocumentUpdate);
    }

    // Also listen to Tiptap editor changes for more granular control
    const handleEditorUpdate = action(() => {
      this.hasLocalChanges = true;
      if (this.status === "offline" || this.status === "offline-unsynced") {
        updateConnectionStatus();
      }
    });

    // We'll set up the editor listener when the editor is available
    this.setupEditorChangeListener = handleEditorUpdate;

    // Set initial status
    this.isBrowserOffline = !navigator.onLine;
    updateConnectionStatus();
  }

  setupEditorChangeTracking(editor?: TiptapEditor) {
    if (this.setupEditorChangeListener && editor) {
      editor.on("update", this.setupEditorChangeListener);
    }
  }

  markLocalChanges() {
    this.hasLocalChanges = true;
  }

  private scheduleHideStatus(delayMs: number = 3000) {
    if (this.hideStatusTimeout) {
      clearTimeout(this.hideStatusTimeout);
    }

    this.hideStatusTimeout = setTimeout(
      action(() => {
        this.showCurrentStatus = false;
        this.hideStatusTimeout = undefined;
      }),
      delayMs,
    );
  }
}
